import ICON_POI from "assets/icon/icon_poi.svg";
import axios from 'axios';
import { FEATURE_SPLIT_LENGTH, FEATURE_TYPES, LAYER, LAYER_PROPS, LAYER_ZINDEX } from 'constants/Constants';
import { OlDefaultView } from 'data/Ol';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { FeatureLike } from 'ol/Feature';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Geometry, MultiPolygon, Point } from 'ol/geom';
import Polygon from 'ol/geom/Polygon';
import { SketchCoordType } from 'ol/interaction/Draw.js';
import VectorLayer from "ol/layer/Vector";
import { getRenderPixel } from 'ol/render';
import RenderEvent from 'ol/render/Event';
import VectorSource from 'ol/source/Vector';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import { useCallback, useRef } from 'react';
import { CustomLayerInfo, customLayerStore } from 'stores/CustomLayer';
import { mapStore } from 'stores/Map';
import { SystemStore } from "stores/System";
import { FeatureTypes } from 'types/TFeature';
import { TRequestContent } from 'types/TRequest';
import { FeatureProperties, TChangeDetectFeature, TFeature, TResult, TResultFeatureCollection } from 'types/TResult';
import { convert4326To3857, onUpdateBBox } from 'utils/OlUtil';
import { LAYER_FACTORY, TLayerFactory, WebGLLayerFactory } from 'utils/WebGLLayerFactory';
import { useKamcoTextLabels } from './useKamcoTextLabels';
import { useMapImages } from './useMapImages';
import { useMapLayerManagement } from "./useMapLayerManagement";

export const useOpenLayers = () => {
    const { getMap, map, getVectorSource, compareImageLayer } = mapStore();
    const { setCustomLayerInfos } = customLayerStore();
    const { targetWMTSLayer } = mapStore();
    const swipeRef = useRef<number>(50);
    const { isKamcoDemo } = SystemStore();
    
    // 분리된 훅들 사용
    const { clearLayers, findLayer, updateLayerOpacity, updateLayerVisible, findResultLayer, findCustomLayer, findKamcoCustomLayer, updateLayerVisibleByName } = useMapLayerManagement();
    const { clearBaseImage, clearCompareImage } = useMapImages();
    const { addKamcoTextLabels, clearTextLayers } = useKamcoTextLabels();

    // const sourceMapper = {
    //     object: objectSource,
    //     change: changeSource,
    //     change2: change2Source,
    //     road: roadSource,
    //     building: buildingSource,
    //     custom: customSource,
    // };

    // const layerMapper = {
    //     object: objectLayer,
    //     change: changeLayer,
    //     change2: change2Layer,
    //     road: roadLayer,
    //     building: buildingLayer,
    //     custom: customLayer
    // };


    const drawRectangle = (coordinates: SketchCoordType) => {
        const start = coordinates[0] as number[];
        const end = coordinates[1] as number[];
        const minX = Math.min(start[0], end[0]);
        const maxX = Math.max(start[0], end[0]);
        const minY = Math.min(start[1], end[1]);
        const maxY = Math.max(start[1], end[1]);

        return new Polygon([
            [
                [minX, minY],
                [maxX, minY],
                [maxX, maxY],
                [minX, maxY],
                [minX, minY],
            ],
        ]);
    };

    const drawBBox = (request: TRequestContent, isFit: boolean = true) => {
        clearAll();
        const first = request.requestImageDataList[0];
        if (!first) return;
        const geometry = drawRectangle([convert4326To3857([first.swLng, first.swLat]), convert4326To3857([first.neLng, first.neLat])]);
        const feature = new Feature({ geometry, name: request.name });
        feature.setStyle(onUpdateBBox);
        getVectorSource()?.addFeature(feature);        
        // isFit && getMap()?.getView().fit(geometry, { padding: [170, 50, 150, 500] });
        isFit && getMap()?.getView().fit(geometry);
    };

    const createChangeDetectFeatures = (features: Array<GeoJSONFeatureCollection>) => {
        return features.map((feat: TChangeDetectFeature) => {
            const geo = new MultiPolygon(
                feat.geometry.coordinates.map((coords) => {
                    return coords.map((multiPoly) => {
                        return multiPoly.map((vec) => {
                            return convert4326To3857(vec);
                        });
                    });
                })
            );

            const feature = new Feature(geo);
            setupPropertiesAtFeature(feature, feat.properties);
            return feature;
        });
    };

    const createFeatures = (features: Array<GeoJSONFeatureCollection>) => {
        return features.map((feat: TFeature) => {
            const geo = new Polygon(
                feat.geometry.coordinates.map((coords) => {
                    return coords.map((vec) => {
                        return convert4326To3857(vec);
                    });
                })
            );

            const feature = new Feature(geo);
            setupPropertiesAtFeature(feature, feat.properties);
            return feature;
        });
    };

    const setupPropertiesAtFeature = (feature: Feature, properties: FeatureProperties) => {
        for (const [key, value] of Object.entries(properties)) {
            feature.set(key, value);
        }
    };


    const drawResults = async (detail: TResult, type: TLayerFactory) => {
        clearFeatures();
        const featureCollections = detail.featureCollections;
        if (detail.customLayerList.length > 0) {
            await addCustomFeaturesLayer(detail.customLayerList);
        } else {
            setCustomLayerInfos([]);
        }
        for (const index in featureCollections) {
            const features = new GeoJSON().readFeatures(featureCollections[index], { featureProjection: 'EPSG:3857' });
            if (getFeatureType(features[0]) === FEATURE_TYPES.POINT) {
                addPointFeaturesLayer(features, type, Number(index));
            } else {
                addSpliteFeaturesLayer({ features, type, index: Number(index), className: featureCollections[index].class, text: isKamcoDemo ? '4차' : undefined });
            }
        }
    }

    const drawKamcoCustomResults = async (featureCollections: TResultFeatureCollection[]) => {
        if (!isKamcoDemo) return;
        
        for (const index in featureCollections) {
            const features = new GeoJSON().readFeatures(featureCollections[index], { featureProjection: 'EPSG:3857' });
            if (getFeatureType(features[0]) === FEATURE_TYPES.POINT) {
                addPointFeaturesLayer(features, LAYER_FACTORY.KAMCO_CUSTOM, Number(index));
            } else {
                addSpliteFeaturesLayer({ features, type: LAYER_FACTORY.KAMCO_CUSTOM, index: Number(index), className: featureCollections[index].class, text: '6차' });
            }
        }
    }


    const addCompareLayerEvent = () => {
        compareImageLayer?.on('prerender', onPrerender);
        compareImageLayer?.on('postrender', onPostRender);
    };

    const addWMTSLayerEvent = () => {
        targetWMTSLayer?.on('prerender', onPrerender);
        targetWMTSLayer?.on('postrender', onPostRender);
    }

    const addPointFeaturesLayer = (features: Feature<Geometry>[], type: TLayerFactory, index?: number) => {
        const layer = createNewGLLayer(LAYER_FACTORY.POINT);
        if (!layer) return;
        index !== undefined && layer.set(LAYER_PROPS.LAYER_NAME, makeLayerName(type, index));
        layer.set(LAYER_PROPS.LAYER_TYPE, LAYER.RESULT_LAYER);
        (layer.getSource() as VectorSource).addFeatures(features);
        map?.addLayer(layer);
    }

    const addSpliteFeaturesLayer = ({features, type, index, className, text}: {features: Feature<Geometry>[], type: TLayerFactory, index?: number, className?: string, text?: '4차' | '6차'}) => {
        let length = features.length;
        while (length > 0) {
            if (type === LAYER_FACTORY.CUSTOM && (index === undefined || index === null)) return;
            const layer = createNewGLLayer(type, index, className);
            if (!layer) return;
            index !== undefined && layer.set(LAYER_PROPS.LAYER_NAME, makeLayerName(type, index));
            if (type === LAYER_FACTORY.KAMCO_CUSTOM) {
                layer.set(LAYER_PROPS.LAYER_TYPE, LAYER.KAMCO_CUSTOM_LAYER);
            } else {
                layer.set(LAYER_PROPS.LAYER_TYPE, type === 'custom' ? LAYER.CUSTOM_LAYER : LAYER.RESULT_LAYER);
            }
            const split = features.splice(0, FEATURE_SPLIT_LENGTH);
            (layer.getSource() as VectorSource).addFeatures(split);
            map?.addLayer(layer);
            
            if (isKamcoDemo && text && type !== LAYER_FACTORY.POINT && index !== undefined) {
                const layerName = makeLayerName(type, index);
                addKamcoTextLabels(split, layerName, text);
            }
            
            length = features.length;
        }
    };


    const addCustomFeaturesLayer = async (geojsonUrls: string[]) => {
        let customLayers: CustomLayerInfo[] = [];

        for(const url of geojsonUrls) {
            const result = await axios.get(url);
            if (result) {
                const layerInfo: CustomLayerInfo = {
                    name: result.data.name,
                    featureCount: result.data.features.length,
                };
                customLayers.push(layerInfo);
                const features = new GeoJSON().readFeatures(result.data, {featureProjection: 'EPSG:3857'});
                addSpliteFeaturesLayer({ features, type: LAYER_FACTORY.CUSTOM, index: geojsonUrls.indexOf(url) });
            }
        }
        setCustomLayerInfos(customLayers);
    }

    const onPrerender = useCallback((event: RenderEvent) => {
        const ctx = event.context;
        const swipeValue = Number(swipeRef.current);

        if (ctx instanceof WebGLRenderingContext) {
            ctx.enable(ctx.SCISSOR_TEST);
            const mapSize = map?.getSize();
            if (!mapSize) return;
            const bottomLeft = getRenderPixel(event, [0, mapSize[1]]);
            const topRight = getRenderPixel(event, [mapSize[0], 0]);
            const width = Math.round((topRight[0] - bottomLeft[0]) * (swipeValue / 100));
            const height = topRight[1] - bottomLeft[1];
            ctx.scissor(width, bottomLeft[1], topRight[0] - width, height);
        } else if (ctx instanceof CanvasRenderingContext2D) {
            const mapSize = map?.getSize();
            if (!mapSize) return;
            const width = mapSize[0] * (swipeValue / 100);
            const tl = getRenderPixel(event, [width, 0]);
            const tr = getRenderPixel(event, [mapSize[0], 0]);
            const bl = getRenderPixel(event, [width, mapSize[1]]);
            const br = getRenderPixel(event, mapSize);

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(tl[0], tl[1]);
            ctx.lineTo(bl[0], bl[1]);
            ctx.lineTo(br[0], br[1]);
            ctx.lineTo(tr[0], tr[1]);
            ctx.closePath();
            ctx.clip();
        }
    }, [map]);

    const onPostRender = useCallback((event: RenderEvent) => {
        if (!event) return;
        const ctx = event.context;
        if (ctx instanceof WebGLRenderingContext) {
            ctx.disable(ctx.SCISSOR_TEST);
        } else if (ctx instanceof CanvasRenderingContext2D) {
            ctx.restore();
        }
    }, [map]);

    const createNewGLLayer = (type: TLayerFactory, index?: number, className?: string) => {
        const source = new VectorSource({ wrapX: false });
        const layer = new WebGLLayerFactory().getGLLayerByType(type, { source }, index, className);
        return layer;
    };

    const makeLayerName = (type: TLayerFactory, name: number | string) => {
        return `${type}_LAYER_${name}`;
    }

    const clearAll = () => {
        getVectorSource()?.clear();
        clearFeatures();
        clearBaseImage();
        clearCompareImage();
        clearOverlays()
    };

    const clearFeatures = () => {
        const resultLayers = findResultLayer();
        const customLayers = findCustomLayer();
        const kamcoCustomLayers = findKamcoCustomLayer();
        resultLayers && clearLayers(resultLayers);
        customLayers && clearLayers(customLayers);
        kamcoCustomLayers && clearLayers(kamcoCustomLayers);
        
        clearTextLayers();
        
        updateLayerOpacity(100);
        updateLayerVisible(true, false);
    };

    const removeCompareLayerEvent = () => {
        compareImageLayer?.hasListener('prerender') && compareImageLayer.un('prerender', onPrerender);
        compareImageLayer?.hasListener('postrender') && compareImageLayer.un('postrender', onPostRender);
    }

    const removeWMTSLayerEvent = () => {
        targetWMTSLayer?.hasListener('prerender') && targetWMTSLayer.un('prerender', onPrerender);
        targetWMTSLayer?.hasListener('postrender') && targetWMTSLayer.un('postrender', onPostRender);
    }


    const clearOverlays = () => {
        map?.getOverlays().clear();
    };

    const rerender = () => {
        map?.render();
    };

    const initSwipe = () => {
        swipeRef.current && (swipeRef.current = 50);
    }

    const initMap = () => {
        map?.getView().setCenter(OlDefaultView.center)
        map?.getView().setZoom(OlDefaultView.zoom);
    }


    const getFeatureType = (feature: FeatureLike) => {
        const values = feature.getProperties();
        return values.geometry.getType() as FeatureTypes;
    }

    const setViewAnimate = (coordinate: Coordinate) => {
        const duration = 2000;
        const view = map?.getView();
        const zoom = view?.getZoom();
        if (!view || !zoom) return;
        view.animate({ center: coordinate, duration: duration });
        view.animate(
        { zoom: zoom - 0.5, duration: duration / 2, },
        { zoom: zoom > 18 ? zoom : 18, duration: duration / 2, }
        );
    }


    const setPoiIcon = (coordinate: Coordinate) => {
        clearOverlays();
        const iconFeature = new Feature({ geometry: new Point(coordinate) });
        const iconStyle = new Style({
            image: new Icon({
                anchor: [0.5, 50],
                anchorXUnits: 'fraction',
                anchorYUnits: 'pixels',
                src: ICON_POI,
            })
        })
        iconFeature.setStyle(iconStyle);
        
        const vectorSource = new VectorSource({ features: [iconFeature], });
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            zIndex: LAYER_ZINDEX.POI,
        });

        vectorLayer.set(LAYER_PROPS.LAYER_TYPE, LAYER.POI_LAYER);
        map?.addLayer(vectorLayer);
    }

    const clearPoiLayer = () => {
        const layer = findLayer(LAYER_PROPS.LAYER_TYPE, LAYER.POI_LAYER);
        layer && map?.removeLayer(layer);
    }


    return {
        map,
        swipeRef,
        drawBBox,
        drawResults,
        drawKamcoCustomResults,
        clearAll,
        clearFeatures,
        clearOverlays,
        rerender,
        initSwipe,
        initMap,
        addCompareLayerEvent,
        removeCompareLayerEvent,
        addWMTSLayerEvent,
        removeWMTSLayerEvent,
        makeLayerName,
        getFeatureType,
        setViewAnimate,
        clearPoiLayer,
        setPoiIcon
    };
};
